import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Select, Button, Table, Icon, Modal, Input } from "common-components";
import { Breadcrumb } from "components";
import { fetchMachine, FetchEventType, FetchStates } from "machines/fetchMachine";
import { useMachine } from "@xstate/react";
import { getBankSelection, getErrorQuestionReport, updateQuestionAvailability } from "api/bank";
import { setSelectOptions, getStartDay, getEndDay } from "utils/common";
import { DatePicker, Image } from "antd";
import openNotificationWithIcon from "utils/hooks/useNotification";
import { UiErrorQuestionReportPage, UiPopupContainer } from "./ErrorQuestionReport.style";
import moment from "moment";

const { RangePicker } = DatePicker;

const dateFormat = "YYYY-MM-DD";

const QUESTION_ERROR_TYPE = {
	None: "無",
	Content: "試題內容有誤",
	Answer: "答案有誤",
	Range: "超出課綱範圍",
	Suggestion: "試題修改建議",
	Management: "管理人員停用",
};

const AVAILABILITY = {
	onlineTest: "線上",
	paper: "紙本",
	quick: "速測",
};

const breadcrumbList = [
	{
		icon: "find_in_page",
		name: "錯題回報",
		path: "/errorQuestionReport",
	},
];

const tableArgs = {
	isLoading: false,
	isSticky: true,
};

const tableColumn = {
	serviceRoot: {
		name: "下架產品",
		defaultValue: "-",
	},
	edu: {
		name: "學制",
		defaultValue: "",
	},
	subject: {
		name: "科目",
		defaultValue: "",
	},
	createTime: {
		name: "回報時間",
		defaultValue: "",
	},
	uid: {
		name: "題目ID",
		defaultValue: "",
	},
	cms: {
		name: "題目CMS編號",
		defaultValue: "",
	},
	questionImage: {
		name: "題目內容",
		default: "",
	},
	source: {
		name: "題目出處",
		defaultValue: "",
	},
	userAccount: {
		name: "下架帳號",
		defaultValue: "無帳號(免登入)",
	},
	questionErrorType: {
		name: "錯誤類型",
		defaultValue: "",
	},
	message: {
		name: "錯誤原因",
		defaultValue: "-",
	},
	availability: {
		name: "測驗別狀態",
		defaultValue: "",
	},
};

const defaultStartDate = getStartDay(moment().toDate());
const defaultEndDate = getEndDay(moment().add(1, "week").toDate());

/** @deprecated 請改用 views/ErrorReportPage/ErrorReportPage */
export const ErrorQuestionReportPage = () => {
	const tableRef = useRef(null);

	const [showModal, setShowModal] = useState(false);
	const selectedQuestion = useRef();

	const [dateRange, setDateRange] = useState([defaultStartDate, defaultEndDate]);
	const startDateTime = moment(dateRange[0]).startOf("day").toISOString();
	const endDateTime = moment(dateRange[1]).endOf("day").toISOString();

	const [sendFormData, setSendFormData] = useState({
		UID: "",
		serviceRoot: "",
		education: "",
		subject: "",
		version: "",
		year: "",
	});

	const [stateSelect, sendSelect] = useMachine(fetchMachine, {
		services: {
			fetchData: async (_context, event) => {
				const result = await getBankSelection();
				const {
					data: { yearMap, eduMap, eduVersion, eduSubject },
				} = result;
				return {
					eduSubject: eduSubject || {},
					eduVersion: eduVersion || {},
					eduMap: eduMap ? setSelectOptions(eduMap) : [],
					yearMap: yearMap ? setSelectOptions(yearMap) : [],
				};
			},
		},
	});

	const { eduSubject, eduVersion, eduMap, yearMap } = stateSelect.context.result || {};

	const eduVersionOption = useMemo(() => {
		if (!sendFormData.education || !eduVersion) return [];
		return setSelectOptions(eduVersion[`${sendFormData.education}`]);
	}, [sendFormData.education]);

	const eduSubjectOption = useMemo(() => {
		if (!sendFormData.education || !eduSubject) return [];
		return setSelectOptions(eduSubject[`${sendFormData.education}`]);
	}, [sendFormData.education]);

	const onSelectChange = (key, value) => {
		setSendFormData({
			...sendFormData,
			[key]: value,
		});
	};

	const [fetchQuestionsEventState, sendQuestionEvent] = useMachine(fetchMachine, {
		services: {
			fetchData: async (_context, event) => {
				const result = await getErrorQuestionReport(event.payload);
				let { questionDeprecationStatisticsDetail: data } = result.data;
				return data;
			},
		},
	});

	const questions = fetchQuestionsEventState?.context?.result || [];

	const fetchQuestions = (payload) => {
		sendQuestionEvent(FetchEventType.Fetch, {
			payload,
		});
	};

	useEffect(() => {
		sendSelect(FetchEventType.Fetch);
		fetchQuestions({
			startDateTime,
			endDateTime,
		});
	}, []);

	const { UID, serviceRoot, education, subject } = sendFormData;

	const fetchQuestionPayload = {
		UID: UID,
		ServiceRoot: serviceRoot,
		Edu: education,
		Subject: subject,
		StartDateTime: startDateTime,
		EndDateTime: endDateTime,
	};

	useEffect(() => {
		if (stateSelect.matches(FetchStates.Resolved)) {
			// const nowYear = yearMap.find((item) => item.value === `${calStudentYear()}`);
			setSendFormData({
				...sendFormData,
				// year: nowYear ? nowYear.value : yearMap[0].value,
				education: eduMap[0].value,
			});
		}
	}, [stateSelect.value]);

	const handleDateChange = (date, dateString) => {
		setDateRange(dateString);
	};

	const displayItem = (key, item) => {
		let content = item[key] || tableColumn[key].defaultValue;

		if (key === "questionErrorType") {
			content = QUESTION_ERROR_TYPE[content] || content;
		}

		if (key === "createTime") {
			content = moment(content).format("YYYY-MM-DD HH:mm");
		}

		if (key === "availability") {
			content = (
				<>
					{Object.entries(content).map(([key, val]) => {
						// availability
						if (AVAILABILITY[key]) {
							return (
								<span key={key} className={`availability ${val ? "green" : "red"}`}>
									{AVAILABILITY[key]}
								</span>
							);
						}
					})}
				</>
			);
		}

		return (
			<Table.Item className="tableItem" key={key}>
				{content}
			</Table.Item>
		);
	};

	const deprecateQuestion = useCallback(
		async (question) => {
			try {
				const availability = Object.keys(question?.availability || {}).reduce((acc, key) => {
					acc[key] = false;
					return acc;
				}, {});

				const res = await updateQuestionAvailability(question.eduSubject, question.uid, availability);
				const status = res.data ? "success" : "error";
				const message = res.data ? "下架成功" : "下架失敗";
				openNotificationWithIcon(status, message);
				fetchQuestions(fetchQuestionPayload);
			} catch (error) {
				openNotificationWithIcon("error", error.message);
			} finally {
				setShowModal(false);
			}
		},
		[fetchQuestionPayload],
	);

	return (
		<>
			<UiErrorQuestionReportPage>
				<div className="breadBlock">
					<Breadcrumb data={breadcrumbList} />
				</div>
				<div className="searchToolBar">
					<div className="selectSearchGroup">
						<div>
							<Input
								type="text"
								placeholder="題目 UID"
								// row
								// maxLength={30}
								value={sendFormData.UID}
								onChange={(e) => {
									onSelectChange("UID", e.target.value);
								}}
							/>
						</div>
						<div className="dateFilterBox">
							<RangePicker
								value={[moment(dateRange[0], dateFormat), moment(dateRange[1], dateFormat)]}
								onChange={handleDateChange}
								allowClear={false}
							/>
						</div>
						{/* <Select
							placeholder="請選擇學年度"
							options={yearMap}
							value={sendFormData.year}
							onChange={(val) => {
								onSelectChange("year", val);
							}}
						/> */}
						<Select
							placeholder="請選擇學制"
							options={eduMap}
							value={sendFormData.education}
							onChange={(val) => {
								onSelectChange("education", val);
							}}
						/>
						{/* {eduVersionOption.length > 0 ? (
							<Select
								placeholder="請選擇版本"
								options={eduVersionOption}
								value={sendFormData.version}
								onChange={(val) => {
									onSelectChange("version", val);
								}}
							/>
						) : (
							""
						)} */}
						{eduSubjectOption.length > 0 ? (
							<Select
								placeholder="請選擇科目"
								options={eduSubjectOption}
								value={sendFormData.subject}
								onChange={(val) => {
									onSelectChange("subject", val);
								}}
							/>
						) : (
							""
						)}
						<Button.IconButton
							variant="blue"
							full={false}
							icon={
								<i className="material-icons" style={{ fontSize: "20px" }}>
									search
								</i>
							}
							onClick={() => fetchQuestions(fetchQuestionPayload)}>
							搜尋
						</Button.IconButton>
					</div>
				</div>
				<div className="questionSearchContent">
					<div className="questionSearchMain">
						<div className="questionSearchTableContent">
							<div className="questionSearchTable" ref={tableRef}>
								<Table {...tableArgs}>
									{questions?.length > 0 ? (
										<Table.Body isSticky={tableArgs.isSticky} isLoading={tableArgs.isLoading}>
											<Table.Row isSticky={tableArgs.isSticky}>
												<Table.Item className={"tableItem checkTableItem"}>
													<div className="tableCheckHeader">圖片</div>
												</Table.Item>
												{Object.entries(tableColumn).map(([key, val]) => (
													<Table.Item className={"tableItem"} key={key}>
														{val.name}
													</Table.Item>
												))}
											</Table.Row>
											{questions.map((item, index) => (
												<Table.Row key={`${item.uid}${index}`}>
													<Table.Item className="tableItem checkTableItem">
														<div className="preview-img">
															<Image
																src={item?.questionImage || "error"}
																preview={{
																	getContainer: document.getElementById("image-preview"),
																}}
																fallback="data:image/png;base64,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"
															/>
														</div>
													</Table.Item>
													{Object.entries(tableColumn).map(([key, val]) => displayItem(key, item))}
													<Table.Item className="tableItem checkTableItem">
														<span className="clickable delete">
															<Icon
																name="delete_forever"
																type="outLined"
																onClick={() => {
																	selectedQuestion.current = item;
																	setShowModal(true);
																}}
															/>
														</span>
													</Table.Item>
												</Table.Row>
											))}
										</Table.Body>
									) : (
										<div className="tableNoData">
											<img src={`/assets/noData.png`} alt="noData" />
											無資料顯示
										</div>
									)}
								</Table>
							</div>
						</div>
					</div>
				</div>
				<UiPopupContainer id="image-preview"></UiPopupContainer>
				{selectedQuestion.current && (
					<Modal.WarningModal
						message={`確定要下架此題目(${selectedQuestion.current.uid})嗎？`}
						visible={showModal}
						onOk={() => {
							deprecateQuestion(selectedQuestion.current);
						}}
						onCancel={() => {
							setShowModal(false);
						}}
					/>
				)}
			</UiErrorQuestionReportPage>
		</>
	);
};
